// 1.2.17: 收藏服务 - 管理用户收藏的餐厅，添加前端缓存优化
import { supabase } from '../lib/supabase';
import type { Restaurant } from '../../features/search/types/restaurant';
import { getCurrentUser } from './authService';
import { logger } from '../utils/logger';

// localStorage key for pending favorites
const PENDING_FAVORITES_KEY = 'pending_favorites';

// 1.2.17: 收藏缓存相关常量
const FAVORITES_CACHE_KEY_PREFIX = 'favorites_cache_';
const CACHE_TTL = 30 * 24 * 60 * 60 * 1000; // 1个月，30天
const CACHE_VERSION = '1.2.17'; // 缓存版本，用于版本兼容性

// 1.2.17: 缓存数据结构
interface FavoritesCache {
  userId: string;
  favorites: Restaurant[];
  timestamp: number;
  version: string;
}

// ==================== Favorites Cache Management (localStorage) ====================

/**
 * 1.2.17: 获取缓存键名
 */
function getCacheKey(userId: string): string {
  return `${FAVORITES_CACHE_KEY_PREFIX}${userId}`;
}

/**
 * 1.2.17: 获取收藏缓存
 */
function getFavoritesCache(userId: string): FavoritesCache | null {
  try {
    const cacheKey = getCacheKey(userId);
    const data = localStorage.getItem(cacheKey);
    if (!data) {
      return null;
    }
    return JSON.parse(data) as FavoritesCache;
  } catch (error: any) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error getting favorites cache:', error);
    }
    // 缓存解析失败，清除无效缓存
    clearFavoritesCache(userId);
    return null;
  }
}

/**
 * 1.2.17: 设置收藏缓存
 */
function setFavoritesCache(userId: string, favorites: Restaurant[]): void {
  try {
    const cacheKey = getCacheKey(userId);
    const cache: FavoritesCache = {
      userId,
      favorites,
      timestamp: Date.now(),
      version: CACHE_VERSION,
    };
    localStorage.setItem(cacheKey, JSON.stringify(cache));
    if (import.meta.env.DEV) {
      logger.log(`✅ Cached ${favorites.length} favorites for user ${userId}`);
    }
  } catch (error: any) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error setting favorites cache:', error);
    }
  }
}

/**
 * 1.2.17: 清除收藏缓存
 */
function clearFavoritesCache(userId: string): void {
  try {
    const cacheKey = getCacheKey(userId);
    localStorage.removeItem(cacheKey);
    if (import.meta.env.DEV) {
      logger.log(`✅ Cleared favorites cache for user ${userId}`);
    }
  } catch (error: any) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error clearing favorites cache:', error);
    }
  }
}

/**
 * 1.2.17: 验证缓存有效性
 */
function isCacheValid(cache: FavoritesCache | null, userId: string): boolean {
  if (!cache) {
    return false;
  }
  
  // 检查用户ID是否匹配
  if (cache.userId !== userId) {
    return false;
  }
  
  // 检查版本是否匹配（版本不匹配时清除旧缓存）
  if (cache.version !== CACHE_VERSION) {
    if (import.meta.env.DEV) {
      logger.log(`⚠️ Cache version mismatch (${cache.version} vs ${CACHE_VERSION}), clearing cache`);
    }
    clearFavoritesCache(userId);
    return false;
  }
  
  // 检查缓存是否过期
  const now = Date.now();
  const age = now - cache.timestamp;
  if (age > CACHE_TTL) {
    if (import.meta.env.DEV) {
      logger.log(`⚠️ Cache expired (age: ${Math.round(age / 1000 / 60)} minutes), clearing cache`);
    }
    clearFavoritesCache(userId);
    return false;
  }
  
  return true;
}

/**
 * Get all favorites for the current authenticated user
 * Returns empty array if user is not authenticated
 * 1.2.17: 优化 - 先返回缓存数据，后台同步最新数据
 */
export async function getFavorites(): Promise<Restaurant[]> {
  try {
    const user = await getCurrentUser();
    
    if (!user) {
      if (import.meta.env.DEV) {
        logger.log('⚠️ User not authenticated, returning empty favorites');
      }
      return [];
    }

    // 1.2.17: 先检查缓存
    const cache = getFavoritesCache(user.id);
    let cachedFavorites: Restaurant[] = [];
    
    if (isCacheValid(cache, user.id)) {
      cachedFavorites = cache!.favorites;
      if (import.meta.env.DEV) {
        logger.log(`📦 Using cached favorites (${cachedFavorites.length} items)`);
      }
      
      // 1.2.17: 后台异步同步最新数据（不阻塞返回）
      syncFavoritesFromBackend(user.id).catch((error) => {
        if (import.meta.env.DEV) {
          logger.error('❌ Error syncing favorites from backend:', error);
        }
        // 后台同步失败不影响已返回的缓存数据
      });
      
      // 立即返回缓存数据
      return cachedFavorites;
    }

    // 1.2.17: 缓存无效或不存在，从后端获取
    return await syncFavoritesFromBackend(user.id);
  } catch (error: any) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error in getFavorites:', error);
    }
    
    // 1.2.17: 如果后端请求失败，尝试使用缓存（即使可能过期）
    const user = await getCurrentUser();
    if (user) {
      const cache = getFavoritesCache(user.id);
      if (cache && cache.userId === user.id) {
        if (import.meta.env.DEV) {
          logger.log('⚠️ Using stale cache due to backend error');
        }
        return cache.favorites;
      }
    }
    
    return [];
  }
}

/**
 * 1.2.17: 从后端同步收藏数据并更新缓存
 */
async function syncFavoritesFromBackend(userId: string): Promise<Restaurant[]> {
  const { data, error } = await supabase
    .from('restaurant_favorites')
    .select('restaurant_data')
    .eq('auth_user_id', userId)
    .order('created_at', { ascending: false });

  if (error) {
    if (import.meta.env.DEV) {
      logger.error('❌ Error fetching favorites from backend:', error);
    }
    throw error;
  }

  // Extract restaurant objects from restaurant_data JSONB field
  const favorites = (data || []).map((item: any) => item.restaurant_data as Restaurant);
  
  // 1.2.17: 更新缓存
  setFavoritesCache(userId, favorites);
  
  if (import.meta.env.DEV) {
    logger.log(`✅ Synced ${favorites.length} favorites from backend`);
  }
  
  return favorites;
}

/**
 * Add a restaurant to favorites
 * Only works for authenticated users
 * Returns success status and error message if any
 */
export async function addFavorite(
  restaurant: Restaurant
): Promise<{ success: boolean; error: string | null }> {
  try {
    const user = await getCurrentUser();
    
    if (!user) {
      return {
        success: false,
        error: '请先登录以收藏餐厅',
      };
    }

    // 1.2.15: 先检查认证状态，避免 RLS 阻止时返回 406
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      logger.error('❌ No valid session for favorite query:', {
        userId: user.id,
        restaurantId: restaurant.id,
        sessionError: sessionError?.message
      });
      return {
        success: false,
        error: '用户未认证，请重新登录',
      };
    }

    // 1.2.15: 验证请求的 userId 与当前认证用户匹配
    if (session.user.id !== user.id) {
      logger.error('❌ User ID mismatch:', {
        requestedUserId: user.id,
        authenticatedUserId: session.user.id,
        restaurantId: restaurant.id
      });
      return {
        success: false,
        error: '无权访问该用户收藏',
      };
    }

    // 1.2.15: 使用 .maybeSingle() 替代 .single()，避免 RLS 阻止时返回 406
    const { data: existing, error: queryError } = await supabase
      .from('restaurant_favorites')
      .select('id')
      .eq('auth_user_id', user.id)
      .eq('restaurant_id', restaurant.id)
      .maybeSingle(); // 使用 maybeSingle() 而不是 single()

    // 1.2.15: 处理 406 错误
    if (queryError) {
      if (queryError.message?.includes('406') || queryError.code === '406') {
        logger.error('❌ 406 Not Acceptable - RLS 策略可能阻止了访问:', {
          code: queryError.code,
          message: queryError.message,
          userId: user.id,
          restaurantId: restaurant.id,
          hasSession: !!session,
          sessionUserId: session?.user?.id
        });
        return {
          success: false,
          error: '访问被拒绝，请检查认证状态',
        };
      }
      // 其他错误（如 PGRST116 表示未找到，这是正常的）
      if (queryError.code !== 'PGRST116') {
        logger.error('❌ Error checking existing favorite:', queryError);
      }
    }

    if (existing) {
      logger.log('⚠️ Restaurant already favorited');
      return {
        success: false,
        error: '该餐厅已在收藏夹中',
      };
    }

    const { error } = await supabase
      .from('restaurant_favorites')
      .insert({
        auth_user_id: user.id,
        restaurant_id: restaurant.id,
        restaurant_data: restaurant,
      });

    if (error) {
      if (import.meta.env.DEV) {
        logger.error('❌ Error adding favorite:', error);
      }
      return {
        success: false,
        error: error.message || '添加收藏失败',
      };
    }

    // 1.2.17: 更新缓存 - 添加到缓存列表
    const cache = getFavoritesCache(user.id);
    if (cache && isCacheValid(cache, user.id)) {
      // 检查是否已存在（避免重复）
      const exists = cache.favorites.some(fav => fav.id === restaurant.id);
      if (!exists) {
        cache.favorites.unshift(restaurant); // 添加到开头（最新）
        setFavoritesCache(user.id, cache.favorites);
        if (import.meta.env.DEV) {
          logger.log('✅ Updated cache: added favorite');
        }
      }
    } else {
      // 缓存无效，清除并重新获取（后台）
      clearFavoritesCache(user.id);
      syncFavoritesFromBackend(user.id).catch(() => {
        // 后台同步失败不影响操作成功
      });
    }

    if (import.meta.env.DEV) {
      logger.log('✅ Added favorite:', restaurant.name);
    }
    return { success: true, error: null };
  } catch (error: any) {
    logger.error('❌ Error in addFavorite:', error);
    return {
      success: false,
      error: error.message || '添加收藏失败',
    };
  }
}

/**
 * Remove a restaurant from favorites
 * Only works for authenticated users
 */
export async function removeFavorite(
  restaurantId: string
): Promise<{ success: boolean; error: string | null }> {
  try {
    const user = await getCurrentUser();
    
    if (!user) {
      return {
        success: false,
        error: '请先登录',
      };
    }

    const { error } = await supabase
      .from('restaurant_favorites')
      .delete()
      .eq('auth_user_id', user.id)
      .eq('restaurant_id', restaurantId);

    if (error) {
      if (import.meta.env.DEV) {
        logger.error('❌ Error removing favorite:', error);
      }
      return {
        success: false,
        error: error.message || '取消收藏失败',
      };
    }

    // 1.2.17: 更新缓存 - 从缓存列表移除
    const cache = getFavoritesCache(user.id);
    if (cache && isCacheValid(cache, user.id)) {
      cache.favorites = cache.favorites.filter(fav => fav.id !== restaurantId);
      setFavoritesCache(user.id, cache.favorites);
      if (import.meta.env.DEV) {
        logger.log('✅ Updated cache: removed favorite');
      }
    } else {
      // 缓存无效，清除并重新获取（后台）
      clearFavoritesCache(user.id);
      syncFavoritesFromBackend(user.id).catch(() => {
        // 后台同步失败不影响操作成功
      });
    }

    if (import.meta.env.DEV) {
      logger.log('✅ Removed favorite:', restaurantId);
    }
    return { success: true, error: null };
  } catch (error: any) {
    logger.error('❌ Error in removeFavorite:', error);
    return {
      success: false,
      error: error.message || '取消收藏失败',
    };
  }
}

/**
 * Check if a restaurant is favorited
 * Returns false if user is not authenticated
 */
export async function isFavorite(restaurantId: string): Promise<boolean> {
  try {
    const user = await getCurrentUser();
    
    if (!user) {
      return false;
    }

    // 1.2.15: 先检查认证状态，避免 RLS 阻止时返回 406
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError || !session) {
      logger.error('❌ No valid session for favorite check:', {
        userId: user.id,
        restaurantId,
        sessionError: sessionError?.message
      });
      return false;
    }

    // 1.2.15: 验证请求的 userId 与当前认证用户匹配
    if (session.user.id !== user.id) {
      logger.error('❌ User ID mismatch:', {
        requestedUserId: user.id,
        authenticatedUserId: session.user.id,
        restaurantId
      });
      return false;
    }

    // 1.2.15: 使用 .maybeSingle() 替代 .single()，避免 RLS 阻止时返回 406
    const { data, error } = await supabase
      .from('restaurant_favorites')
      .select('id')
      .eq('auth_user_id', user.id)
      .eq('restaurant_id', restaurantId)
      .maybeSingle(); // 使用 maybeSingle() 而不是 single()

    if (error) {
      // 1.2.15: 处理 406 错误
      if (error.message?.includes('406') || error.code === '406') {
        logger.error('❌ 406 Not Acceptable - RLS 策略可能阻止了访问:', {
          code: error.code,
          message: error.message,
          userId: user.id,
          restaurantId,
          hasSession: !!session,
          sessionUserId: session?.user?.id
        });
        return false;
      }
      // If no row found, it's not favorited (not an error)
      if (error.code === 'PGRST116') {
        return false;
      }
      logger.error('❌ Error checking favorite:', error);
      return false;
    }

    return !!data;
  } catch (error: any) {
    logger.error('❌ Error in isFavorite:', error);
    return false;
  }
}

/**
 * Get favorite count for the current authenticated user
 * Returns 0 if user is not authenticated
 */
export async function getFavoriteCount(): Promise<number> {
  try {
    const user = await getCurrentUser();
    
    if (!user) {
      return 0;
    }

    const { count, error } = await supabase
      .from('restaurant_favorites')
      .select('*', { count: 'exact', head: true })
      .eq('auth_user_id', user.id);

    if (error) {
      logger.error('❌ Error getting favorite count:', error);
      return 0;
    }

    return count || 0;
  } catch (error: any) {
    logger.error('❌ Error in getFavoriteCount:', error);
    return 0;
  }
}

// ==================== Pending Favorites Management (localStorage) ====================

/**
 * Add a restaurant to pending favorites (for anonymous users)
 * Stores in localStorage until user logs in
 */
export function addPendingFavorite(restaurant: Restaurant): void {
  try {
    const pending = getPendingFavorites();
    // Check if already in pending list
    const exists = pending.some((r: Restaurant) => r.id === restaurant.id);
    if (!exists) {
      pending.push(restaurant);
      localStorage.setItem(PENDING_FAVORITES_KEY, JSON.stringify(pending));
      logger.log('✅ Added to pending favorites:', restaurant.name);
    }
  } catch (error: any) {
    logger.error('❌ Error adding pending favorite:', error);
  }
}

/**
 * Get all pending favorites from localStorage
 */
export function getPendingFavorites(): Restaurant[] {
  try {
    const data = localStorage.getItem(PENDING_FAVORITES_KEY);
    if (!data) {
      return [];
    }
    return JSON.parse(data) as Restaurant[];
  } catch (error: any) {
    logger.error('❌ Error getting pending favorites:', error);
    return [];
  }
}

/**
 * Clear pending favorites from localStorage
 */
export function clearPendingFavorites(): void {
  try {
    localStorage.removeItem(PENDING_FAVORITES_KEY);
    logger.log('✅ Cleared pending favorites');
  } catch (error: any) {
    logger.error('❌ Error clearing pending favorites:', error);
  }
}

/**
 * Process pending favorites after user logs in
 * Adds all pending favorites to the database
 * Clears pending list after successful processing
 */
export async function processPendingFavorites(): Promise<{
  success: boolean;
  processed: number;
  errors: string[];
}> {
  try {
    const user = await getCurrentUser();
    
    if (!user) {
      logger.log('⚠️ User not authenticated, cannot process pending favorites');
      return {
        success: false,
        processed: 0,
        errors: ['用户未登录'],
      };
    }

    const pending = getPendingFavorites();
    
    if (pending.length === 0) {
      logger.log('ℹ️ No pending favorites to process');
      return {
        success: true,
        processed: 0,
        errors: [],
      };
    }

    logger.log(`🔄 Processing ${pending.length} pending favorites...`);

    const errors: string[] = [];
    let processed = 0;

    // Process each pending favorite
    for (const restaurant of pending) {
      const result = await addFavorite(restaurant);
      if (result.success) {
        processed++;
      } else {
        errors.push(`${restaurant.name}: ${result.error}`);
      }
    }

    // Clear pending list after processing
    if (processed > 0) {
      clearPendingFavorites();
      logger.log(`✅ Processed ${processed} pending favorites`);
    }

    return {
      success: errors.length === 0,
      processed,
      errors,
    };
  } catch (error: any) {
    logger.error('❌ Error processing pending favorites:', error);
    return {
      success: false,
      processed: 0,
      errors: [error.message || '处理待收藏餐厅失败'],
    };
  }
}

